import React from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Animated modal with backdrop and content transitions.
 * Use for payment overlays, dialogs, and full-screen modals.
 * @see .cursor/ui.md
 */
const AnimatedModal = ({
  isOpen,
  onClose,
  children,
  className = "",
  contentClassName = "",
  backdropClassName = "bg-black/40 backdrop-blur-sm",
  zIndex = "z-50", // "z-50" | "z-[9999]" for payment overlays
  closeOnBackdrop = false,
  rootProps = {},
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={`fixed inset-0 flex items-center justify-center p-4 ${zIndex} ${backdropClassName}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={closeOnBackdrop ? onClose : undefined}
          {...rootProps}
        >
          <motion.div
            className={`min-w-[280px] min-h-[120px] !rounded-2xl ${contentClassName}`}
            initial={{ opacity: 0, y: -24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Payment processing overlay — polished spinner + message.
 * Use for CompanyInfoModal, AuthContext rzpPaymentProcessing, etc.
 */
export const ProcessingOverlay = ({
  isOpen,
  message = "Processing your payment…",
  hint = "Do not close or refresh this browser tab.",
  zIndex = "z-[9999]",
}) => (
  <AnimatedModal
    isOpen={isOpen}
    contentClassName="flex flex-col items-center bg-white shadow-xl border border-gray-100 rounded-2xl overflow-hidden"
    zIndex={zIndex}
  >
    <div className="flex flex-col items-center px-10 py-10 w-full max-w-sm">
      {/* Bouncing dots loader */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-2.5 h-2.5 rounded-full bg-primary"
            animate={{ y: [0, -8, 0] }}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.1,
            }}
          />
        ))}
      </div>

      <p className="font-heading text-lg font-semibold text-gray-900 text-center mb-1">
        {message}
      </p>
      <p className="text-sm text-gray-500 text-center mb-5">
        This usually takes a few seconds
      </p>

      {hint && (
        <div className="w-full flex items-start gap-3 px-4 py-3 rounded-xl bg-gray-50 border border-gray-100">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <svg
              className="w-4 h-4 text-primary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed pt-0.5">{hint}</p>
        </div>
      )}
    </div>
  </AnimatedModal>
);

export default AnimatedModal;
